// ============================================================
// ANSICHTSSACHE-AGENT – Markieren und die Sammelleiste
// ============================================================
// Mehrere Zeilen auf einmal: ein Kaestchen je Zeile, eines im
// Spaltenkopf fuer alle gezeigten, und unten die Sammelleiste
// mit dem, was mit allen Markierten geschieht. Wie in MOCO.
//
// DIE MARKIERUNG STEHT IM BROWSER, NICHT IN DER ADRESSE. Die
// Regel „alles Steuernde in die Adresse" gilt fuer das, was die
// Ansicht bestimmt — Filter, Zeitraum, Umfang. Eine Markierung
// ist ein Griff fuer die naechsten Sekunden; niemand verschickt
// „diese elf Haken" als Lesezeichen.
//
// WAS NICHT MEHR DASTEHT, IST NICHT MEHR MARKIERT. Faellt eine
// Zeile aus dem Filter oder ins Archiv, zaehlt die Leiste sie
// nicht weiter mit — sonst archivierte der naechste Klick etwas,
// das man gar nicht mehr sieht.
//
// SHIFT MARKIERT DEN BEREICH vom zuletzt angeklickten Kaestchen
// bis zu diesem, wie in jedem Dateimanager.
//
// Die Leiste schwebt knapp UEBER der Summenzeile, mittig — sie
// soll die Summe nicht verdecken — und verschwindet mit der
// letzten Markierung von selbst.
// ============================================================

"use client";

import { useRef, useState } from "react";
import { X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { TableCell, TableHead } from "@/components/ui/table";
import { formatDecimal } from "@/lib/number-format";

/** Die Markierung einer Liste — auf die gezeigten Zeilen beschnitten. */
export function useMarkierung(gezeigteIds: string[]) {
  const [gesetzt, setGesetzt] = useState<string[]>([]);
  const letzte = useRef<number | null>(null);

  // Beschnitten wird beim Lesen und nicht in einem Effekt: nach
  // einem Auffrischen stuende sonst fuer einen Bildaufbau noch
  // eine Zeile in der Zaehlung, die es nicht mehr gibt.
  const da = new Set(gezeigteIds);
  const markiert = gesetzt.filter((id) => da.has(id));
  // Nachschlagen per Menge: `istMarkiert` laeuft fuer jede Zeile,
  // mit `includes` waeren das bei 5.000 markierten Zeilen 25 Mio.
  // Vergleiche je Bildaufbau.
  const markiertSet = new Set(markiert);
  const alle = gezeigteIds.length > 0 && markiert.length === gezeigteIds.length;

  function umschalten(id: string, bereich: boolean) {
    const index = gezeigteIds.indexOf(id);
    const an = !markiertSet.has(id);
    const ids =
      bereich && letzte.current !== null
        ? gezeigteIds.slice(Math.min(letzte.current, index), Math.max(letzte.current, index) + 1)
        : [id];
    letzte.current = index;
    const betroffen = new Set(ids);
    setGesetzt((vorher) => {
      const rest = vorher.filter((x) => da.has(x) && !betroffen.has(x));
      return an ? [...rest, ...ids] : rest;
    });
  }

  return {
    markiert,
    istMarkiert: (id: string) => markiertSet.has(id),
    umschalten,
    alle,
    alleUmschalten: () => setGesetzt(alle ? [] : gezeigteIds),
    /** Mehrere auf einmal an oder ab — alle Zeilen einer Gruppe. */
    gruppeSetzen: (ids: string[], an: boolean) => {
      letzte.current = null;
      const betroffen = new Set(ids);
      setGesetzt((vorher) => {
        const rest = vorher.filter((x) => da.has(x) && !betroffen.has(x));
        return an ? [...rest, ...ids.filter((x) => da.has(x))] : rest;
      });
    },
    leeren: () => {
      letzte.current = null;
      setGesetzt([]);
    },
  };
}

type Markierung = ReturnType<typeof useMarkierung>;

/** Das Kaestchen im Spaltenkopf: alle gezeigten Zeilen. */
export function MarkierKopf({ markierung }: { markierung: Markierung }) {
  const teilweise = markierung.markiert.length > 0 && !markierung.alle;
  return (
    <TableHead className="w-px pr-0">
      <Checkbox
        checked={markierung.alle}
        onChange={markierung.alleUmschalten}
        aria-checked={teilweise ? "mixed" : markierung.alle}
        aria-label={markierung.alle ? "Markierung aufheben" : "Alle markieren"}
        title={markierung.alle ? "Markierung aufheben" : "Alle gezeigten markieren"}
        className="align-middle"
      />
    </TableHead>
  );
}

/**
 * Das Kaestchen in der Zeile.
 *
 * Es liegt auf `z-[1]`, ueber der unsichtbaren Flaeche, die die
 * Zeile zum Verweis macht — sonst oeffnete der Haken das Blatt.
 * Die ganze Zelle ist die Klickflaeche, nicht nur die 16 Pixel.
 */
export function MarkierZelle({
  markierung,
  id,
  label,
}: {
  markierung: Markierung;
  id: string;
  /** Wofuer der Haken steht — fuer den Bildschirmleser. */
  label: string;
}) {
  return (
    <TableCell className="w-px py-0 pr-0">
      <label className="relative z-[1] flex h-full cursor-pointer items-center py-3.5">
        <Checkbox
          checked={markierung.istMarkiert(id)}
          // Der Klick und nicht `onChange`: nur er weiss, ob
          // Shift gedrueckt war.
          readOnly
          onClick={(e) => markierung.umschalten(id, e.shiftKey)}
          aria-label={`${label} markieren`}
        />
      </label>
    </TableCell>
  );
}

/**
 * Die Leiste unten: wie viele markiert sind, was mit ihnen
 * geschehen kann, und das Kreuz, das die Markierung aufhebt.
 */
export function Sammelleiste({
  anzahl,
  wort,
  zusatz,
  onLeeren,
  children,
}: {
  anzahl: number;
  /** Einzahl und Mehrzahl — „Ausgabe", „Ausgaben". */
  wort: [string, string];
  /** Was die Zahl ergaenzt, etwa die Summe der Markierten. */
  zusatz?: React.ReactNode;
  onLeeren: () => void;
  /** Die Knoepfe. */
  children: React.ReactNode;
}) {
  if (anzahl === 0) return null;

  return (
    <div
      role="region"
      aria-label="Markierte Zeilen"
      className="glass-strong border-border shadow-e4 fixed bottom-16 left-1/2 z-40 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-xl border py-2 pr-2 pl-4"
    >
      {/* Die Knoepfe gehen vor: wird es eng, kuerzt sich der Zusatz,
          statt dass „Rechnung erstellen…“ zweizeilig umbricht. */}
      <span className="text-body-sm text-text-primary min-w-0 truncate font-medium whitespace-nowrap">
        {formatDecimal(anzahl)} {anzahl === 1 ? wort[0] : wort[1]} markiert
        {zusatz && (
          <span className="text-text-tertiary ml-2 font-normal tabular-nums">{zusatz}</span>
        )}
      </span>
      <span className="bg-border h-5 w-px shrink-0" aria-hidden />
      <div className="flex shrink-0 items-center gap-2 [&>*]:whitespace-nowrap">{children}</div>
      <button
        type="button"
        onClick={onLeeren}
        aria-label="Markierung aufheben"
        title="Markierung aufheben"
        className="text-text-tertiary hover:bg-bg-secondary hover:text-text-primary duration-fast grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
